/**
 * Shared number formatting utilities for currency, numbers, and percentages.
 *
 * Used across the frontend (components, stores, composables, utils) and
 * potentially the backend for consistent number display formatting.
 *
 * Decimal precision strategy:
 * - Dashboard cards and totals use 0 decimals: formatCurrency(value)     → "$1,234"
 * - Detail views and field displays use 2:     formatCurrency(value, 2)  → "$1,234.56"
 * - Charts/compact displays use abbreviation:  formatCurrencyCompact(value) → "$1.2M"
 */

/**
 * Format a number as USD currency.
 *
 * @param value - Number or numeric string to format (null/undefined returns "$0")
 * @param decimals - Fraction digits (default: 0 for dashboard totals, use 2 for detail views)
 * @returns Formatted string like "$1,234" or "$1,234.56"
 */
export function formatCurrency(value: null | number | string | undefined, decimals = 0): string {
  if (value === null || value === undefined) return "$0";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "$0";
  return new Intl.NumberFormat("en-US", {
    currency: "USD",
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
    style: "currency",
  }).format(num);
}

/**
 * Format a number as compact USD currency with M/K abbreviation.
 * Best suited for charts and dashboard cards where space is limited.
 *
 * @param value - Number to format
 * @returns "$1.2M", "$1.2K", or "$500"
 */
export function formatCurrencyCompact(value: number): string {
  if (typeof value !== "number" || isNaN(value)) return "$0";
  if (value >= 1000000) {
    return "$" + (value / 1000000).toFixed(1) + "M";
  }
  if (value >= 1000) {
    return "$" + (value / 1000).toFixed(1) + "K";
  }
  return "$" + value.toFixed(0);
}

/**
 * Format a number with thousand separators.
 *
 * @param value - Number or numeric string to format (null/undefined returns "0")
 * @returns Formatted string like "1,234,567"
 */
export function formatNumber(value: null | number | string | undefined): string {
  if (value === null || value === undefined) return "0";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "0";
  return new Intl.NumberFormat("en-US").format(num);
}

/**
 * Format a number as a percentage.
 *
 * @param value - The percentage value (e.g., 40 for 40%)
 * @param decimals - Fraction digits (default: 0)
 * @returns Formatted string like "40%" or "40.5%"
 */
export function formatPercent(value: null | number | string | undefined, decimals = 0): string {
  if (value === null || value === undefined) return "0%";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "0%";
  return `${decimals > 0 ? num.toFixed(decimals) : num}%`;
}

/**
 * Parse a currency/number string to a number.
 * Strips $, commas, and other non-numeric characters.
 *
 * @param value - String like "$1,234.56" or number (null/undefined returns 0)
 * @returns Parsed number, or 0 if invalid
 */
export function parseCurrency(value: null | number | string | undefined): number {
  if (value === null || value === undefined) return 0;
  return parseFloat(value.toString().replace(/[$,]/g, "")) || 0;
}
